import React, { useState, useEffect } from "react";
import { useLocation, useHistory } from "react-router-dom";
import PropTypes from "prop-types";
import { IntlProvider } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider } from "styled-components";
import Header from "../animo-player/header";
import vmTheme from "../constants/theme";
import ContentValue from "../constants/lang/en.json";
import PublishContainer from "./publish-container";
import Upgrade from "../containers/upgrade/upgrade";
import { getCredits, getProjectInfo, getUserDetails, setProjectDetails, setUserPaymentDetails } from "../redux/actions/projectUtils";
import { getIPData, getPlanDetails, getPresetItems, intercom, setAnimoPlayer, setAnimoPlayerId } from "../redux/actions/appUtils";
import openUrl from "../helper/openUrl";
import Toast from "../common-components/Toast";
import SupportForm from "../containers/upgrade/supportForm";
import Loader from "../common-components/Loader";
import AppConfig, { INTERCOM_APP_ID } from "../constants/config";
import useNotify from "../helper/hooks/useNotify";
import proceedWait from "../helper/proceedWait";
import { sendSocketMessage } from "../socket";
import SocketName from "../constants/socket-channels";
import { loadFallbackCss } from "../helper/subtitleHelper";
import PriceModal from "../containers/payments/priceModal";
import PaymentModal from "../containers/payments/paymentModal";

const Publisher = ({ match, isPublish }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { warn } = useNotify();

  const [isLoading, setIsLoading] = useState(true);
  const [progress, setProgress] = useState(10);

  const appReducer = useSelector((state) => state.app);
  const theme = appReducer.get("theme");
  const { shortId } = location.state || {};

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
    script.async = true;
    script.onload = () => {

      // Call your backend intercom function to get proper JWT
      intercom().then(response => {
        if (response && response.token) {
          window.Intercom('boot', {
            app_id: INTERCOM_APP_ID,
            intercom_user_jwt: response.token
          });
        }
      }).catch(error => {
        console.error('Intercom authentication failed:', error);
      });
    };
    document.head.appendChild(script);

    // Cleanup
    return () => {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
    };
  }, []);

  useEffect(() => {
    if (!isPublish) {
      sendSocketMessage(SocketName.S_PROJECT_TRACKER, {
        renderLoaded: true
      });
    }
  }, [isPublish]);

  useEffect(() => {
    const { params = { id: "" } } = match;
    getUserDetails().then((userDetailsRes) => {
      setProgress(75);
      loadFallbackCss();
      dispatch(getCredits());
      dispatch(getPresetItems());
      dispatch(getIPData());
      dispatch(getPlanDetails());
      dispatch(setAnimoPlayerId("animo-player"));
      dispatch(setProjectDetails(params.id, userDetailsRes, !isPublish, isPublish)).then((res) => {
        setProgress(95);
        dispatch(getProjectInfo(params.id));
        dispatch(setUserPaymentDetails(isPublish ? res.projectId : params.id));
        const hasShorts = res.project.shorts && Object.keys(res.project.shorts).length !== 0;
        if (hasShorts && !shortId) {
          openUrl(`${window.location.origin}/shorts/${params.id}`, true);
        }
        if (isPublish && userDetailsRes.userId !== res.userId) {
          warn("You don't have access to this project.");
          openUrl(AppConfig.API_URL, true);
        } else {
          setIsLoading(false);
        }
      }).catch(async () => {
        warn("You don't have access to this project.");
        await proceedWait(3000);
        openUrl(AppConfig.API_URL, true);
      });
    }).catch(() => {
      // dispatch(setProjectDetails(params.id, {}, false));
      setIsLoading(false);
      warn("Please login to access to this project");
      const { pathname } = window.location;
      const match = pathname.match(/\/(render|preview|publish)\/([a-z0-9]+)\/?$/i);
      const pathSegment = match ? match[1] : null;
      const projectId = match ? match[2] : null;
      window.location.href = `/logout?path=${pathSegment}/${projectId}&language=en`;
    });
    dispatch(setAnimoPlayer({ isAnimoPlayer: true }));

    const cleanUp = history.listen((location) => {
      const basePath = location.pathname.split('/')[1];
      if (['render', 'publish', 'shorts', 'preview'].includes(basePath)) {
        dispatch(setAnimoPlayer({ isAnimoPlayer: true }));
      } else {
        dispatch(setAnimoPlayer({ isAnimoPlayer: false }));
        window.location.reload(); // to trigger all the api in editor.
      }
    });
    return () => {
      cleanUp();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IntlProvider locale="en" messages={{ ...ContentValue }}>
      <ThemeProvider theme={vmTheme[theme]}>
        <PriceModal />
        <Header isExport={true} />
        <PublishContainer isExport={true} isPublish={isPublish} shortId={shortId} />
        <Upgrade isPremium={true} />
        <SupportForm />
        <PaymentModal />
        <Toast />
        {isLoading && <Loader progress={progress} />}

      </ThemeProvider>
    </IntlProvider>
  );
};

Publisher.propTypes = {
  match: PropTypes.object,
  isPublish: PropTypes.bool,
};
export default Publisher;
